import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography } from 'shared/ui/Typography'
import { IconButton } from 'shared/ui/IconButton'
import { Textarea } from 'shared/ui'
import { AgentKnowledgeBase } from 'pages/chatbot/ui/AgentKnowledgeBase/AgentKnowledgeBase'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
  WidgetItem,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import {
  KB_ENABLE_OPTIONS,
  KnowledgeBaseWidgetStore,
  STEP_HEADERS,
} from './store/knowledgeBaseWidgetStore'
import styles from './styles.module.scss'

export const KnowledgeBaseWidget = observer(() => {
  const { chatStore, knowledgeBaseSectionStore } = useUnifiedAgentContext()
  const [store] = useState(() => new KnowledgeBaseWidgetStore(knowledgeBaseSectionStore, chatStore))

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  useWidgetKeyboard({
    onNavigate: store.step === 1 ? (dir) => store.moveFocus(dir) : undefined,
    onEnter: store.step === 1 ? store.handleSelect : undefined,
    onSubmit: store.isSubmitDisabled ? undefined : store.handleSubmit,
    onNext: store.step === 2 && !store.isNextDisabled ? store.advanceStep : undefined,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const hintItems =
    store.step === 1
      ? ['↑↓ to navigate', 'Enter to select', 'Esc to skip']
      : getLastStepHintMessage([])

  const renderContent = () => {
    switch (store.step) {
      case 1:
        return (
          <div className={styles.list}>
            {KB_ENABLE_OPTIONS.map((option, index) => {
              const isFocused = index === store.safeIndex

              return (
                <WidgetItem
                  key={option.id}
                  isFocused={isFocused}
                  badgeIndex={index}
                  onClick={() => store.selectAt(index)}
                >
                  <Typography variant={'body-rg-regular'} ariaLabel={option.label}>
                    {option.label}
                  </Typography>
                </WidgetItem>
              )
            })}
          </div>
        )
      case 2:
        return (
          <div className={styles.kbStep}>
            <AgentKnowledgeBase
              isOnlyButton
              knowledgeBaseUsage={store.localUseKnowledgeBase}
              agentKnowledgeBaseStore={knowledgeBaseSectionStore.agentKnowledgeBaseStore}
              setKnowledgeBaseUsage={store.setLocalUseKnowledgeBase}
              setKnowledgeBaseDocuments={store.setLocalDocuments}
              knowledgeBaseDocuments={store.localDocuments}
              isShowToggle={false}
            />
          </div>
        )
      case 3:
        return (
          <div className={styles.descriptionStep}>
            <Textarea
              value={store.information}
              onChange={store.setInformation}
              placeholder='Enter instructions'
              rows={2}
              className={styles.textarea}
            />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <>
      <WidgetCard header={STEP_HEADERS[store.step - 1]}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            store.step === 2 ? (
              <IconButton
                icon={'arrowRight'}
                ariaLabel={'WidgetNext'}
                color={'primary'}
                onClick={store.advanceStep}
                disabled={store.isNextDisabled}
              />
            ) : store.isLastStep ? (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
                disabled={store.isSubmitDisabled}
              />
            ) : null
          }
        />
      </WidgetCard>
      <WidgetHint items={hintItems} />
    </>
  )
})
